import type { BatchRunSummary, TeBaseline, TeSpec } from "@/types";

/**
 * A TE is a diff from the dev deployment (fe/TEST_CASES_BATCH.md §3.2, new
 * artifact). The screen never asks for a full configuration — it asks what
 * should differ, and the diff is the TE's identity: two runs that changed
 * the same things are the same TE, however far the deployment moved between
 * them.
 *
 * These mirror BE `eval_te_spec` — the canonical form here must match the
 * one BE stores, or a TE splits into two columns.
 */

/** Only what differs from the deployment; restating a default changes nothing. */
export function normalizeSpec(spec: TeSpec, baseline: TeBaseline | null): TeSpec {
  const out: TeSpec = {};
  if (spec.model && spec.model !== baseline?.default_model) out.model = spec.model;
  if (spec.code) out.code = spec.code;
  if (spec.history != null && spec.history !== (baseline?.history_limit ?? 50)) {
    out.history = spec.history;
  }
  if (spec.force != null && spec.force !== (baseline?.force_response ?? false)) {
    out.force = spec.force;
  }
  return out;
}

/**
 * One string per TE. The commit subject is a label the screen kept when the
 * commit was picked, not a condition, so it does not split a TE.
 */
export function specKey(spec: TeSpec): string {
  const comparable: Record<string, unknown> = {};
  for (const key of Object.keys(spec).sort()) {
    const value = spec[key as keyof TeSpec];
    if (value === undefined) continue;
    if (key === "code" && spec.code) {
      const code: Record<string, unknown> = {};
      for (const k of Object.keys(spec.code).sort()) {
        if (k !== "subject") code[k] = spec.code[k as keyof typeof spec.code];
      }
      comparable.code = code;
    } else {
      comparable[key] = value;
    }
  }
  return JSON.stringify(comparable);
}

export type SpecAxis = {
  /** What changed — "모델", "코드", … */
  label: string;
  /** The deployment's value. */
  from: string;
  /** This TE's value. */
  to: string;
};

export function modelLabel(id: string | null | undefined, baseline: TeBaseline | null): string {
  if (!id) return "—";
  return baseline?.models.find((m) => m.id === id)?.label ?? id;
}

export function shortSha(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 7) : "—";
}

/** The changed axes, in the order the editor shows them. */
export function specAxes(spec: TeSpec, baseline: TeBaseline | null): SpecAxis[] {
  const out: SpecAxis[] = [];
  if (spec.model) {
    out.push({
      label: "모델",
      from: modelLabel(baseline?.default_model, baseline),
      to: modelLabel(spec.model, baseline),
    });
  }
  if (spec.code) {
    out.push({
      label: "코드",
      from: baseline?.deployed_sha ? `${shortSha(baseline.deployed_sha)} (배포본)` : "배포본",
      to: `${shortSha(spec.code.git_ref)} · ${spec.code.code_resolution}`,
    });
  }
  if (spec.history != null) {
    out.push({
      label: "history_limit",
      from: String(baseline?.history_limit ?? 50),
      to: String(spec.history),
    });
  }
  if (spec.force != null) {
    out.push({
      label: "force_response",
      from: String(baseline?.force_response ?? false),
      to: String(spec.force),
    });
  }
  return out;
}

/** Name = what changed. Nobody types it, so two people get the same name. */
export function teName(spec: TeSpec, baseline: TeBaseline | null): string {
  const axes = specAxes(spec, baseline);
  if (axes.length === 0) return "배포본 그대로";
  if (axes.length === 1) return `${axes[0].label} ${axes[0].to}`;
  return `${axes.length} 개 항목 변경`;
}

/** Subtitle = how it changed. */
export function teSub(spec: TeSpec, baseline: TeBaseline | null): string {
  const axes = specAxes(spec, baseline);
  if (axes.length === 0) return "바꾼 항목 없음";
  if (axes.length === 1) return `${axes[0].from} → ${axes[0].to}`;
  return axes.map((a) => `${a.label} ${a.to}`).join(" · ");
}

/**
 * `TE-A`, `TE-B`, … in the order the page first ran them. The first is the
 * page's baseline — what every other column is compared against (§3.2).
 */
export function teLetters(runs: readonly BatchRunSummary[]): Map<string, string> {
  const letters = new Map<string, string>();
  const oldestFirst = [...runs].sort((a, b) => a.created_at.localeCompare(b.created_at));
  for (const run of oldestFirst) {
    const key = specKey(run.te_spec ?? {});
    if (!letters.has(key)) letters.set(key, `TE-${String.fromCharCode(65 + letters.size)}`);
  }
  return letters;
}

/** Shortcuts that fill the selects — a preset is not a TE, the diff still is. */
export function presets(baseline: TeBaseline | null): { label: string; spec: TeSpec }[] {
  const out: { label: string; spec: TeSpec }[] = [{ label: "배포본 그대로", spec: {} }];
  for (const model of (baseline?.models ?? [])
    .filter((m) => m.id !== baseline?.default_model)
    .slice(0, 2)) {
    out.push({ label: `모델 ${model.label}`, spec: { model: model.id } });
  }
  out.push({ label: "history 10", spec: { history: 10 } });
  return out;
}

/**
 * What a run cannot change — kept on screen, folded. When a score will not
 * reproduce, this is where to look ("was it something I could not pick?").
 */
export const FIXED_CONDITIONS: readonly [string, string][] = [
  ["Main Agent · Subagent 프롬프트", "코드 시각(snapshot_at)의 활성 버전이 쓰인다 — 따로 고를 수 없다 (BE-REPLAY-PROMPT-PIN)"],
  ["timezone · geo", "실행 호스트 값이 쓰인다 — 요청에 실을 수 없다"],
  ["retrieval 인덱스", "조회 시점의 인덱스를 그대로 탄다"],
  ["backend 구성", "요청 단위로 갈아끼울 수 없다"],
  ["skills 버전 · branch", "배포에 묶여 있다"],
];
